'use strict';

function escapeHtml(str) {
  return String(str).replace(/[&<>"']/g, (char) => {
    const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
    return map[char];
  });
}

function metaText(item) {
  const parts = [`Priority: ${item.priority}`];
  if (item.dueDate) parts.push(`Due: ${item.dueDate}`);
  if (item.visibility === 'public') parts.push('Public');
  if (item.status === 'done') parts.push('Done');
  return parts.join(' · ');
}

/**
 * Render a clickable inbox list inside `container`. `onToggle` is called
 * with the item id when the user clicks the status indicator.
 */
export function renderUserInbox(container, items, onToggle) {
  if (!container) return;

  if (items.length === 0) {
    container.innerHTML = '<p class="empty">No items yet. Create one to get started.</p>';
    return;
  }

  container.innerHTML = items
    .map(
      (item) => `
        <div class="list-item" data-item-id="${escapeHtml(item.id)}">
          <button class="status-toggle" type="button" aria-label="Toggle status for ${escapeHtml(item.title)}">
            <span class="status-indicator ${item.status === 'done' ? 'done' : 'open'}"></span>
          </button>
          <div class="item-summary">
            <strong>${escapeHtml(item.title)}</strong>
            <span class="meta">${escapeHtml(metaText(item))}</span>
          </div>
        </div>
      `
    )
    .join('');

  container.querySelectorAll('.status-toggle').forEach((button) => {
    button.addEventListener('click', () => {
      const id = button.closest('.list-item')?.dataset.itemId;
      if (id) onToggle?.(id);
    });
  });
}

/**
 * Render the create-item form inside `container`. `onCreate` receives
 * the form fields object when the form is submitted.
 */
export function renderCreateItemForm(container, onCreate) {
  if (!container) return;

  // Re-rendering the form would wipe any partial user input while the user is
  // typing, so keep the existing form once it has been mounted.
  if (container.querySelector('form')) return;

  container.innerHTML = `
    <form id="create-item-form" class="form">
      <label>Title<input type="text" name="title" required placeholder="What needs to be done?"></label>
      <label>Description<textarea name="description" rows="4" placeholder="Add details..."></textarea></label>
      <label>Priority
        <select name="priority">
          <option value="low">Low</option>
          <option value="medium" selected>Medium</option>
          <option value="high">High</option>
        </select>
      </label>
      <label>Due date<input type="date" name="dueDate"></label>
      <label>Visibility
        <select name="visibility">
          <option value="private" selected>Private</option>
          <option value="public">Public</option>
        </select>
      </label>
      <button class="btn-primary" type="submit">Create item</button>
    </form>
  `;

  container.querySelector('form').addEventListener('submit', (event) => {
    event.preventDefault();
    const form = event.target;
    const data = new FormData(form);
    const fields = {
      title: data.get('title').toString().trim(),
      description: data.get('description').toString().trim(),
      priority: data.get('priority').toString(),
      dueDate: data.get('dueDate').toString(),
      visibility: data.get('visibility').toString(),
    };
    if (!fields.title) return;
    onCreate?.(fields);
    form.reset();
  });
}

/**
 * Render three dashboard stats cards.
 */
export function renderDashboardStats(container, stats) {
  if (!container) return;
  container.innerHTML = `
    <div class="stat"><strong>${stats.total}</strong><span>Total items</span></div>
    <div class="stat"><strong>${stats.done}</strong><span>Completed</span></div>
    <div class="stat"><strong>${stats.open}</strong><span>Open</span></div>
  `;
}

/**
 * Render admin overview stats: contributors, items, completed.
 */
export function renderAdminOverviewStats(container, stats) {
  if (!container) return;
  container.innerHTML = `
    <div class="stat"><strong>${stats.contributors}</strong><span>Contributors</span></div>
    <div class="stat"><strong>${stats.total}</strong><span>Items</span></div>
    <div class="stat"><strong>${stats.done}</strong><span>Completed</span></div>
  `;
}
